import { useEffect, useState } from "react";
import { 
  Box, Typography, Container, Card, Stack, Rating, IconButton 
} from "@mui/material";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useNavigate } from "react-router-dom";
import API from "../../api/api";
import GlobalSkeleton from "../../components/GlobalSkeleton"; // <-- Add this here

export default function FeedbackOption() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      const userData = localStorage.getItem("user");
      if (!userData) {
        navigate("/login");
        return;
      }
      
      const user = JSON.parse(userData);
      
      // Safely grab the ID whether it is saved as 'id' or 'user_id'
      const userId = user.id || user.user_id; 

      if (!userId) {
        navigate("/login");
        return;
      }

      try {
        const res = await API.get(`/user/${userId}/reviews`);
        setReviews(res.data);
      } catch (err) {
        console.error("Failed to load reviews", err);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, [navigate]);

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#F8F9FA", pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: "#F8D548",
          borderRadius: "0 0 30px 30px",
          pb: 3,
          pt: 2
        }}
      >
        <Container maxWidth="sm">
 
          <Stack direction="row" alignItems="center" spacing={1}>
 
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: "#4a332e",
                bgcolor: "rgba(255,255,255,0.15)"
              }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
 
            <Typography
              variant="h6"
              fontWeight="800"
              color="#4a332e"
            >
              Your Feedback
            </Typography>
 
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ mt: 3, px: 2 }}>
        {loading ? (
          <Box sx={{ mt: 1 }}>
            {/* The New Global List Skeleton */}
            <GlobalSkeleton type="list" />
          </Box>
        ) : reviews.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 10, opacity: 0.6 }}>
            <ChatBubbleOutlineIcon sx={{ fontSize: 60, mb: 2, color: '#999' }} />
            <Typography variant="h6" fontWeight="700" color="#555">No feedback yet</Typography>
            <Typography variant="body2" color="#777">Orders you rate will appear here.</Typography>
          </Box>
        ) : (
          <Stack spacing={2.5}>
            {reviews.map((review) => (
              <Card 
                key={review.review_id} 
                sx={{ 
                  borderRadius: '24px', 
                  p: 3, 
                  boxShadow: '0 6px 20px rgba(0,0,0,0.04)',
                  border: 'none',
                  bgcolor: '#ffffff'
                }}
              >
                {/* Top Row: Order ID & Date */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="caption" sx={{ color: '#666', fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase' }}>
                    Order #{review.order_id.toString().slice(-5)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#888', fontWeight: 600 }}>
                    {new Date(review.created_at).toLocaleDateString('en-IN', {
                      day: 'numeric',
                      month: 'numeric',
                      year: 'numeric'
                    })}
                  </Typography>
                </Stack>

                {/* Items Summary - Styled exactly like OrderPage */}
                <Stack spacing={0.8} sx={{ mb: 2 }}>
                  {review.items && Array.isArray(review.items) ? (
                    // If backend returns an array of items
                    review.items.map((item, idx) => {
                      const isNonVeg = item.category?.toLowerCase().includes('n') || 
                                       item.name?.toLowerCase().includes('fish') || 
                                       item.name?.toLowerCase().includes('chicken') || 
                                       item.name?.toLowerCase().includes('egg') || 
                                       item.name?.toLowerCase().includes('meat');
                      return (
                        <Stack key={idx} direction="row" spacing={1.2} alignItems="center">
                          <Box sx={{ width: 12, height: 12, border: '1px solid', borderColor: isNonVeg ? '#e44444' : '#00b050', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isNonVeg ? '#e44444' : '#00b050' }} />
                          </Box>
                          <Typography variant="body2" sx={{ color: '#444', fontWeight: 600 }}>
                            {item.quantity || 1} x {item.name}
                          </Typography>
                        </Stack>
                      );
                    })
                  ) : (
                    // Fallback if backend only returns the items_summary string
                    review.items_summary?.split(',').map((itemName, idx) => {
                      const name = itemName.trim();
                      const isNonVeg = name.toLowerCase().includes('fish') || 
                                       name.toLowerCase().includes('chicken') || 
                                       name.toLowerCase().includes('egg') || 
                                       name.toLowerCase().includes('meat');
                      return (
                        <Stack key={idx} direction="row" spacing={1.2} alignItems="center">
                          <Box sx={{ width: 12, height: 12, border: '1px solid', borderColor: isNonVeg ? '#e44444' : '#00b050', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: isNonVeg ? '#e44444' : '#00b050' }} />
                          </Box>
                          <Typography variant="body2" sx={{ color: '#444', fontWeight: 600 }}>
                            1 x {name}
                          </Typography>
                        </Stack>
                      );
                    })
                  )}
                </Stack>
                
                {/* Rating Stars */}
                <Box sx={{ mt: 1 }}>
                  <Rating 
                    value={review.rating} 
                    readOnly 
                    size="small" 
                    sx={{ color: '#FF4B2B' }} 
                  />
                </Box>
                
                {/* Review Comment Box (Conditional) */}
                {review.comment && (
                  <Box sx={{ mt: 1.5, bgcolor: '#FFF0EE', p: 2, borderRadius: '16px' }}>
                    <Typography variant="body2" color="#444" fontStyle="italic" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
                      "{review.comment}"
                    </Typography>
                  </Box>
                )}
              </Card>
            ))}
          </Stack>
        )}
      </Container>
    </Box>
  );
}